import { MinTableKey } from "./table";

export const CONTEXTS = {
  WORK: "work",
  HOME: "home",
} as const;
// Limits the API enforces on todo fields (see pickUpdatable in
// site-backend/utils/todo.ts); the UI uses MAX_TITLE_LENGTH as the title
// inputs' maxLength so a valid submission can't get a 400.
export const MAX_TITLE_LENGTH = 500;
export const MAX_NOTES_LENGTH = 10_000;
export const MAX_ID_LENGTH = 200;

export type Context = (typeof CONTEXTS)[keyof typeof CONTEXTS];

// A repeating todo comes back unchecked `every` units after it was last
// done (doneAt is the completion timestamp). Reset is client-side on load;
// there is no server job. `every` is an integer 1..MAX_REPEAT_EVERY.
// `from` picks what the interval counts from: "done" (the default when
// absent, so older data stays valid) counts from doneAt; "start" counts from
// `anchor`, a YYYY-MM-DD local day that is the last day the repeat came due,
// moved forward on each reset. `anchor` is required for "start" and is only
// stored for it. `day` (1..31) is the day of month a start-mode monthly
// repeat is meant to land on, so an anchor clamped to Feb 28 still comes back
// to the 31st in March; it's only stored for start mode with unit "month".
export const REPEAT_UNITS = ["day", "week", "month"] as const;
export const REPEAT_FROM = ["done", "start"] as const;
export const MAX_REPEAT_EVERY = 365;
export type RepeatUnit = (typeof REPEAT_UNITS)[number];
export type RepeatFrom = (typeof REPEAT_FROM)[number];
export type TodoRepeat = { every: number; unit: RepeatUnit; from?: RepeatFrom; anchor?: string; day?: number };

// A repeat's `day` is absent, or 1..31 and consistent with `anchor` (a
// YYYY-MM-DD already checked by the caller): the anchor sits on `day`, or on
// its month's last day when that month is shorter. Shared by the API's
// validator and the client's reset so both agree.
export const isRepeatDayFor = (day: unknown, anchor: string) => {
  if (day === undefined) return true;
  if (!Number.isInteger(day) || (day as number) < 1 || (day as number) > 31) return false;
  const [y, m, d] = anchor.split("-").map(Number);
  const monthDays = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return d === Math.min(day as number, monthDays);
};

export type TodoData = {
  type: "todo";
  id: string;
  userId: string;
  title: string;
  notes?: string;
  context: Context;
  // A todo with children acts as a project/group; a todo with none is a
  // plain leaf. Nesting is capped at one level, enforced client-side only
  // (a todo that already has children can't itself be given a parentId,
  // and the parent-picker only offers todos with none) — the server just
  // rejects the trivial self-reference case.
  parentId?: string;
  priority?: number;
  complexity?: number;
  done: boolean;
  // ISO, server-set when done goes true. Also the completion timestamp a
  // repeat is counted from; it is left in place on uncheck, so always read
  // it together with `done`.
  doneAt?: string;
  // userId of whoever last checked it (owner or a sharee); cleared when unchecked.
  checkedBy?: string;
  scheduledDate?: string; // YYYY-MM-DD, always client-local — never computed server-side
  dueDate?: string; // YYYY-MM-DD (a real calendar date), client-local like scheduledDate
  repeat?: TodoRepeat; // absent means it doesn't repeat
  order: number;
  timesScheduled: number;
  createdAt: string; // ISO, server-set
  updatedAt: string; // ISO, server-set
};

export type TodoDBData = MinTableKey & TodoData;
